/**
 * React Query hooks for Club Members
 *
 * Handles member management, roles, and permissions.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { membersApi, type Member, type UpdateMemberRolePayload, type PaginatedMembers } from '../lib/api/members';
import { useOroMutation, OroOperation } from './useOroMutation';

/**
 * Fetch members of a club (paginated)
 *
 * Query Key: ['clubMembers', clubSlug]
 * Returns: { members: Member[], pagination: PaginationMeta }
 *
 * Issue #773: Added pagination support (default limit=100 for backwards compat)
 */
export function useClubMembers(clubSlug: string, limit = 100, offset = 0) {
  return useQuery<PaginatedMembers>({
    queryKey: ['clubMembers', clubSlug, { limit, offset }],
    queryFn: () => membersApi.getClubMembers(clubSlug, limit, offset),
    enabled: !!clubSlug,
    staleTime: 1000 * 60, // 1 minute - member lists change occasionally
  });
}

/**
 * Fetch the FULL club member list by paging through (limit caps at 100 per
 * PaginationSchema). Used where client-side search must cover all members, not
 * just the first page (e.g. admin check-in walk-up search, #1978). Pass
 * `enabled: false` to defer the (potentially multi-request) load until needed.
 */
export function useAllClubMembers(clubSlug: string, enabled = true) {
  return useQuery<Member[]>({
    queryKey: ['clubMembers', clubSlug, 'all'],
    queryFn: async () => {
      const all: Member[] = [];
      let offset = 0;
      // Bounded by has_more; pages are 100 each.
      for (;;) {
        const page = await membersApi.getClubMembers(clubSlug, 100, offset);
        all.push(...page.members);
        // A malformed page (missing/non-boolean has_more) must NOT silently
        // stop paging and report success with a partial roster (#2186) — that
        // produces a false-empty walk-up search the consumer reads as success.
        // Throw so the query surfaces isError instead. (Backend always returns
        // a well-formed envelope; this guards the contract, not the happy path.)
        if (typeof page.pagination?.has_more !== 'boolean') {
          throw new Error('Malformed members pagination: missing has_more');
        }
        if (!page.pagination.has_more || page.members.length === 0) break;
        offset += page.members.length;
      }
      return all;
    },
    enabled: enabled && !!clubSlug,
    staleTime: 1000 * 60,
  });
}

/**
 * Update a member's role
 * 
 * On success: Invalidates ['clubMembers', clubSlug] to refresh the list
 */
export function useUpdateMemberRole(clubSlug: string) {
  const queryClient = useQueryClient();
  
  return useOroMutation<void, { userId: string; role: UpdateMemberRolePayload['role'] }>({
    mutationFn: ({ userId, role }: { userId: string; role: UpdateMemberRolePayload['role'] }) =>
      membersApi.updateMemberRole(clubSlug, userId, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clubMembers', clubSlug] });
      queryClient.invalidateQueries({ queryKey: ['club', clubSlug] });
    },
    oro: {
      op: OroOperation.UpdateMemberRole,
      retryPolicy: 'never',
      debugLabel: 'Member role change',
    },
  });
}

/**
 * Remove a member from the club
 * 
 * On success: Invalidates ['clubMembers', clubSlug] to refresh the list
 */
export function useRemoveMember(clubSlug: string) {
  const queryClient = useQueryClient();
  
  return useOroMutation<void, string>({
    mutationFn: (userId: string) => membersApi.removeMember(clubSlug, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clubMembers', clubSlug] });
      queryClient.invalidateQueries({ queryKey: ['club', clubSlug] }); // Update member count
    },
    oro: {
      op: OroOperation.RemoveMember,
      retryPolicy: 'never',
      debugLabel: 'Member removal confirmation',
    },
  });
}
